import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Shield, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/admin-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (response.ok && data.token) {
        localStorage.setItem('auth_token', data.token);
        toast.success('Welcome, Administrator!');
        navigate('/admin');
      } else {
        toast.error(data.detail || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      toast.error('Unable to connect. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#344E41] to-[#1a2e1f] p-4">
      <Card className="w-full max-w-md p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-2xl rounded-2xl">
        <div className="flex flex-col items-center mb-8">
          <div className="p-4 bg-[#3A5A40]/10 rounded-full mb-4">
            <Shield className="w-12 h-12 text-[#3A5A40]" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
            Admin Portal
          </h1>
          <p className="text-[#588157] mt-2">J423 Community Discipleship Center</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-[#344E41] font-medium">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="admin@example.com"
              data-testid="admin-email-input"
              className="mt-1 bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
            />
          </div>
          
          <div>
            <Label htmlFor="password" className="text-[#344E41] font-medium">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              data-testid="admin-password-input"
              className="mt-1 bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
            />
          </div>
          
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 hover:shadow-lg active:scale-95 font-medium rounded-lg py-6 text-lg"
          >
            {loading ? 'Signing in...' : 'Sign In as Admin'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link 
            to="/login" 
            className="inline-flex items-center text-[#588157] hover:text-[#3A5A40] font-medium transition-colors"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Back to User Login
          </Link>
        </div>

        <div className="mt-8 pt-6 border-t border-[#CCD5AE]/50 text-center">
          <p className="text-xs text-[#A3B18A]">
            This portal is restricted to authorized administrators only.
          </p>
        </div>
      </Card>
    </div>
  );
}
